import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import CompanyLandingPage from './CompanyLandingPage';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { loadClerkConfig } from './utils/clerkConfigLoader';
import AIWebinarPage from './AIWebinar/AIWebinarPage';
import AIWebinarPageES from './AIWebinar/AIWebinarPageES';
import AIWebinarTestimonial from './AIWebinar/AIWebinarTestimonial';
import AIWebinarTestimonialES from './AIWebinar/AIWebinarTestimonialES';
import SoftwarePage from './Software/SoftwarePage';
import PaymentSuccess from './Software/PaymentSuccessPage';
import CalendarAIConsultation from './Software/CalendarAIConsultation';
import BusinessConsultation from './Software/BusinessConsultation';
import DirectSellingPage from './DirectSelling/DirectSellingPage';
import ZoomPage from './Zoom/ZoomMeeting';
import ZoomExplanation from './Zoom/ZoomExplanation';
import AsteroidsGame from './Asteroids/AsteroidsGame';
import SpaceshipGame from './Spaceship/SpaceshipGame';
import Space from './Space/Space';
import StarWars from './StarWars/StarWars';
import CustomSoftwarePage from './CustomSoftware/CustomSoftwarePage';
import TaskManagerWrapper from './TaskManager/TaskManagerWrapper';
import TestTaskManager from './TestTaskManager';
import LoginPrompt from './TaskManager/LoginPrompt';
import ResumeEditor from './ResumeEditor/ResumeEditor';
import DemoVideo from './DemoVideo/DemoVideo';
import LandingsIframe from './LandingsIframe';
import InvoiceGenerator from './Invoice/InvoiceGenerator';
import ContractGenerator from './Contract/ContractGenerator';
import ExcelGenerator from './Excel/ExcelGenerator';
import CalendlyRedirect from './CalendlyRedirect';
import TeatroRedirect from './TeatroRedirect';
// import TeatroProxyIframe from './TeatroProxyIframe'; // Alternative with proxy fallback
import EnglishConversationIframe from './EnglishConversationIframe';
import FuturePage from './FuturePage/FuturePage';
import PharmaIframe from './PharmaIframe';
import MashkraftHomePage from './MashkraftHomePage';
import './ResumeEditor/openaiKeyLoader'; // Import the OpenAI API key loader
import config from './config';

// Protected route component - only enforce if auth is enabled
const ProtectedRoute = ({ children, isAuthEnabled }) => {
  // If auth is not enabled, just render the children
  if (!isAuthEnabled) {
    return children;
  }
  
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <LoginPrompt />
      </SignedOut>
    </>
  );
};

// Conditionally render with or without Clerk provider
const AppWithRoutes = ({ isAuthEnabled }) => (
  <Router>
    <Routes>
      <Route path="/" element={<MashkraftHomePage />} />
      <Route path="/classic" element={<CompanyLandingPage />} />
      <Route path="/direct" element={<App />} />
      <Route path="/software" element={<SoftwarePage />} />
      <Route path="/software/payment-success" element={<PaymentSuccess />} />
      <Route path="/calendar-ai-consultation" element={<CalendarAIConsultation />} />
      <Route path="/business-consultation" element={<BusinessConsultation />} />
      <Route path="/calendly" element={<CalendlyRedirect />} />
      <Route path="/teatro" element={<TeatroRedirect />} />
      <Route path="/english-conversation" element={<EnglishConversationIframe />} />
      <Route path="/direct-selling" element={<DirectSellingPage />} />
      <Route path="/ai-webinar" element={<AIWebinarPage />} />
      <Route path="/ai-webinar/es" element={<AIWebinarPageES />} />
      <Route path="/ai-webinar-testimonial" element={<AIWebinarTestimonial />} />
      <Route path="/ai-webinar-testimonial/es" element={<AIWebinarTestimonialES />} />
      <Route path="/zoom" element={<ZoomPage />} />
      <Route path="/zoom-explanation" element={<ZoomExplanation />} />
      <Route path="/asteroids" element={<AsteroidsGame />} />
      <Route path="/spaceship" element={<SpaceshipGame />} />
      <Route path="/space" element={<Space />} />
      <Route path="/star-wars" element={<StarWars />} />
      <Route path="/custom-software" element={<CustomSoftwarePage />} />
      <Route path="/future" element={<FuturePage />} />
      <Route path="/pharma" element={<PharmaIframe />} />
      <Route path="/task-manager" element={
        <ProtectedRoute isAuthEnabled={isAuthEnabled}>
          <TaskManagerWrapper />
        </ProtectedRoute>
      } />
      <Route path="/resume-editor" element={<ResumeEditor />} />
      <Route path="/demo-video" element={<DemoVideo />} />
              <Route path="/invoice" element={
          <ProtectedRoute isAuthEnabled={isAuthEnabled}>
            <InvoiceGenerator />
          </ProtectedRoute>
        } />
        <Route path="/contract" element={
          <ProtectedRoute isAuthEnabled={isAuthEnabled}>
            <ContractGenerator />
          </ProtectedRoute>
        } />
        <Route path="/excel" element={
          <ProtectedRoute isAuthEnabled={isAuthEnabled}>
            <ExcelGenerator />
          </ProtectedRoute>
        } />
        {/* Landings routes - must be at the end to catch all sub-routes */}
      <Route path="/landings" element={<LandingsIframe />} />
      <Route path="/landings/:path1" element={<LandingsIframe />} />
      <Route path="/landings/:path1/:path2" element={<LandingsIframe />} />
      <Route path="/landings/:path1/:path2/:path3" element={<LandingsIframe />} />
    </Routes>
  </Router>
);

// Main App Component that loads Clerk config
const MainApp = () => {
  const [clerkConfig, setClerkConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeClerk = async () => {
      try {
        const config = await loadClerkConfig();
        setClerkConfig(config);
      } catch (error) {
        console.error('Failed to load Clerk configuration:', error);
        setClerkConfig({ available: false, publishableKey: null });
      } finally {
        setLoading(false);
      }
    };

    initializeClerk();
  }, []);

  // Show loading screen while config loads
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading application...
      </div>
    );
  }

  const isAuthEnabled = clerkConfig?.available && clerkConfig?.publishableKey;

  if (!isAuthEnabled) {
    console.warn('Clerk authentication disabled - no valid configuration found');
  }

  // Render with or without Clerk provider based on configuration
  return (
    <React.StrictMode>
      {isAuthEnabled ? (
        <ClerkProvider publishableKey={clerkConfig.publishableKey} afterSignOutUrl='/'>
          <AppWithRoutes isAuthEnabled={isAuthEnabled} />
        </ClerkProvider>
      ) : (
        <AppWithRoutes isAuthEnabled={isAuthEnabled} />
      )}
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<MainApp />);

reportWebVitals();